import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from '../../../base/api';
import { Loading } from '../../../base/enums';
import { appActions } from '../../../base/redux/slices/appSlice';
import { preparePhoneInputConfig } from '../../../base/utils';
import { ConfigResponse } from '../../api/interfaces';
import { workerApi } from '../../api';
import { workerAppActions } from '../slices/workerAppSlice';
import { baseErrorHandler } from '../../../base/redux/sagas/appSaga';
import { City } from '../../../base/interfaces/config';
import { CanceledError } from 'axios';

function* init() {
    try {
        yield put(appActions.setLoading(Loading.App));

        const {
            data: { registrationCountries, seo, citizenshipCountries },
        }: ApiResponse<ConfigResponse> = yield call(workerApi.getConfig);
        const phoneInputConfig = preparePhoneInputConfig(registrationCountries);
        yield put(
            workerAppActions.getConfigSuccess({
                phoneInputConfig,
                seoTags: seo,
                citizenshipCountries,
            }),
        );

        yield put(workerAppActions.setInitialized(true));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* sendConfirmationCode({ payload: { phone, recaptchaToken, callback } }: AnyAction) {
    try {
        yield put(appActions.setLoading(Loading.VerificationCode));
        yield call(workerApi.sendVerificationSMS, { phone, recaptchaToken });
        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

let citySearchRequestController: AbortController | null = null;

function* citySearchDelay({ payload }: PayloadAction<string>) {
    yield delay(300);
    try {
        yield call(citySearch, payload);
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* citySearch(search: string) {
    try {
        yield put(appActions.setLoading(Loading.CityAutocomplete));
        if (citySearchRequestController) {
            citySearchRequestController.abort();
        }
        citySearchRequestController = new AbortController();
        const { data }: ApiResponse<City[]> = yield call(
            workerApi.findCity,
            { search },
            citySearchRequestController.signal,
        );
        yield put(workerAppActions.setCitySearch(data));
        yield put(appActions.setLoading(null));
        citySearchRequestController = null;
    } catch (e) {
        if (!(e instanceof CanceledError)) {
            yield put(appActions.setLoading(null));
            citySearchRequestController = null;
            console.error(e);
        }
    }
}

export function* workerAppSaga() {
    yield takeEvery(workerAppActions.init, init);
    yield takeEvery(workerAppActions.sendConfirmationCode, sendConfirmationCode);
    yield takeEvery(workerAppActions.citySearch, citySearchDelay);
}
